import { Box, Button, Dialog, Flex, Text, TextField, Switch } from '@radix-ui/themes';
import { useState } from 'react';
import { toast } from 'sonner';
import teamtailorLogo from '../../../../style/logos/teamtailor.svg';
import { apiFetch } from '../../../../utils/api';
import { errorMessageFrom } from '../../../../utils/errorMessage';

const TeamtailorConfig = ({
  modelShow,
  modalHandleClose,
  isConfigured,
  isExisting,
  updateIntegrationTable,
  clientId,
  companyName,
  initialApiKey,
  initialProviderKey,
  initialWebhookKey,
}: {
  modelShow: boolean;
  modalHandleClose: () => void;
  isConfigured: boolean;
  isExisting: boolean;
  updateIntegrationTable: (id: string) => void;
  clientId: string | undefined;
  companyName: string;
  initialApiKey: string;
  initialProviderKey: string;
  initialWebhookKey: string;
}) => {
  const [apiKey, setApiKey] = useState(initialApiKey);
  const [integrationKey, setIntegrationKey] = useState(initialProviderKey);
  const [webhookKey, setWebhookKey] = useState(initialWebhookKey);
  const [active, setActive] = useState(isExisting ? isConfigured : true);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const body: { api_key?: string; provider_key?: string; company_webhook_signature_key?: string; active: boolean } =
        {
          active,
        };

      if (apiKey !== '*****') {
        body.api_key = apiKey;
      }

      if (integrationKey !== '*****') {
        body.provider_key = integrationKey;
      }

      if (webhookKey !== '*****') {
        body.company_webhook_signature_key = webhookKey;
      }

      const response = await apiFetch('/integrations/teamtailor', {
        method: 'PUT',
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success('Teamtailor integration updated successfully');
        if (typeof updateIntegrationTable === 'function' && clientId) {
          updateIntegrationTable(clientId);
        }
        modalHandleClose();
      } else {
        toast.error(await errorMessageFrom(response, 'Failed to update Teamtailor integration'));
      }
    } catch (error) {
      console.error('Error updating Teamtailor integration:', error);
      toast.error('An error occurred while updating the integration');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect this integration?')) return;
    setLoading(true);
    try {
      const response = await apiFetch('/integrations/teamtailor', {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Teamtailor integration disconnected');
        if (typeof updateIntegrationTable === 'function' && clientId) {
          updateIntegrationTable(clientId);
        }
        modalHandleClose();
      } else {
        toast.error(await errorMessageFrom(response, 'Failed to disconnect Teamtailor integration'));
      }
    } catch (error) {
      console.error('Error disconnecting Teamtailor integration:', error);
      toast.error('An error occurred while disconnecting the integration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open={modelShow} onOpenChange={modalHandleClose}>
      <Dialog.Content aria-describedby={undefined}>
        <Dialog.Title>Integration settings</Dialog.Title>
        <Box mb="4">
          <Flex align="center" gap="2" mb="3">
            <img src={teamtailorLogo} alt="Teamtailor Logo" style={{ height: '32px', width: '32px' }} />
            <Text size="5" weight="bold">
              Teamtailor
            </Text>
          </Flex>
          <hr className="hr" style={{ margin: '16px 0' }} />
          <Flex direction="column" gap="4">
            <Box>
              <Text as="div" size="2" mb="1" weight="bold">
                Company Name
              </Text>
              <TextField.Root value={companyName} readOnly disabled={loading}>
                <TextField.Slot>
                  <i className="bi bi-building" />
                </TextField.Slot>
              </TextField.Root>
            </Box>
            <Box>
              <Text as="div" size="2" mb="1" weight="bold">
                API Key
              </Text>
              <TextField.Root
                placeholder="Enter Teamtailor API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={loading}
              >
                <TextField.Slot>
                  <i className="bi bi-key" />
                </TextField.Slot>
              </TextField.Root>
            </Box>
            <Box>
              <Text as="div" size="2" mb="1" weight="bold">
                Integration Key
              </Text>
              <TextField.Root
                placeholder="Enter Teamtailor Integration Key"
                value={integrationKey}
                onChange={(e) => setIntegrationKey(e.target.value)}
                disabled={loading}
              >
                <TextField.Slot>
                  <i className="bi bi-link-45deg" />
                </TextField.Slot>
              </TextField.Root>
            </Box>
            <Box>
              <Text as="div" size="2" mb="1" weight="bold">
                Webhook Signature Key
              </Text>
              <TextField.Root
                placeholder="Enter Teamtailor Webhook Signature Key"
                value={webhookKey}
                onChange={(e) => setWebhookKey(e.target.value)}
                disabled={loading}
              >
                <TextField.Slot>
                  <i className="bi bi-shield-lock" />
                </TextField.Slot>
              </TextField.Root>
            </Box>
            {isExisting && (
              <Flex gap="2" align="center">
                <Switch checked={active} onCheckedChange={setActive} disabled={loading} size="3" color="grass" />
                <Text size="2" weight="bold">
                  Active
                </Text>
              </Flex>
            )}
          </Flex>
        </Box>

        <Flex gap="3" mt="6" justify="end">
          {isExisting && (
            <Button
              variant="soft"
              color="red"
              size="3"
              onClick={handleDisconnect}
              disabled={loading}
              style={{ marginRight: 'auto' }}
            >
              Disconnect
            </Button>
          )}
          <Button variant="soft" color="gray" size="3" onClick={modalHandleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={loading} color="grass" size="3">
            Save
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default TeamtailorConfig;
